import React from "react"
import { Box, Typography, IconButton, Button, Tooltip } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined"
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined"
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined"
import EggOutlinedIcon from "@mui/icons-material/EggOutlined"
import SetMealOutlinedIcon from "@mui/icons-material/SetMealOutlined"
import KebabDiningOutlinedIcon from "@mui/icons-material/KebabDiningOutlined"
import { COLORS } from "../../../../utils"

const COMPOSITION_STYLES = {
  VEGE: { label: "Végétarien", bgcolor: COLORS.GREEN_SUCCESS_100, iconColor: COLORS.GREEN_STATUS, Icon: EggOutlinedIcon },
  CARNE: { label: "Viande", bgcolor: COLORS.RED_50, iconColor: COLORS.RED_700, Icon: KebabDiningOutlinedIcon },
  POISSON: { label: "Poisson", bgcolor: COLORS.PRIMARY_BLUE_50, iconColor: COLORS.PRIMARY_COLOR, Icon: SetMealOutlinedIcon },
}

const formatShortDate = (iso) => {
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`
}

const formatProdDates = (prodDates = []) => {
  if (!prodDates.length) return "—"
  const sameMonth = prodDates.every((d) => new Date(d).getMonth() === new Date(prodDates[0]).getMonth())
  if (prodDates.length > 1 && sameMonth) {
    const days = prodDates.map((d) => String(new Date(d).getDate()).padStart(2, "0"))
    const month = String(new Date(prodDates[0]).getMonth() + 1).padStart(2, "0")
    return `${days.join(";")}/${month}`
  }
  return prodDates.map(formatShortDate).join(";")
}

const formatFoodcost = (foodcost) => (
  foodcost === null || foodcost === undefined
    ? "—"
    : `${foodcost.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
)

const getDlcForDate = (recipe, date) => {
  if (recipe.dlcByDate && date !== undefined) return recipe.dlcByDate[date] ?? null
  return recipe.dlc || null
}

const RecipeCard = ({ recipe, displayProdDates, pendingDates = [], onChoosePendingDate, isDone = false, isBlocked = false, onRemove }) => {
  const composition = COMPOSITION_STYLES[recipe.composition]
  const isPending = pendingDates.length > 0
  const prodDatesToShow = displayProdDates !== undefined ? displayProdDates : recipe.prodDates
  const dlcDate = getDlcForDate(recipe, prodDatesToShow ? prodDatesToShow[0] : undefined)
  const cardSx = {
    ...sx.card,
    bgcolor: isPending ? COLORS.RED_50 : (composition ? composition.bgcolor : COLORS.WHITE),
    ...(isPending ? sx.cardPending : {}),
    ...(isDone ? sx.cardDone : {}),
    ...(isBlocked ? sx.cardBlocked : {}),
    ...(onRemove ? { "&:hover .recipeCardRemoveBtn": { opacity: 1 } } : {}),
  }

  const statusIcon = isDone
    ? { Icon: CheckCircleOutlinedIcon, color: COLORS.GREEN_STATUS, label: "Jours de production planifiés" }
    : isPending
      ? { Icon: EventRepeatOutlinedIcon, color: COLORS.RED_700, label: "Jour de production à choisir" }
      : isBlocked
        ? { Icon: HourglassEmptyOutlinedIcon, color: COLORS.ORANGE_800, label: "Choisissez d'abord le jour de production en attente" }
        : composition
          ? { Icon: composition.Icon, color: composition.iconColor, label: composition.label }
          : null

  return (
    <Box sx={cardSx}>
      <Box className="flexRow center spaceBetween">
        <Typography sx={sx.typeLabel}>{recipe.catLabel}</Typography>
        {statusIcon && (
          <Tooltip title={statusIcon.label}>
            <statusIcon.Icon sx={{ ...sx.typeIcon, color: statusIcon.color }} />
          </Tooltip>
        )}
      </Box>
      <Typography sx={sx.ref}>{recipe.code}</Typography>
      <Typography sx={sx.title}>{recipe.name}</Typography>
      
      <Box sx={sx.divider} />

      {isPending ? (
        <>
          <Typography sx={sx.pendingLabel}>Choisis le jour de prod</Typography>
          <Box className="flexRow wrap" sx={sx.pendingRow}>
            {pendingDates.map((date) => (
              <Button
                key={date}
                size="small"
                variant="outlined"
                data-no-drag="true"
                onClick={() => onChoosePendingDate(date)}
                sx={sx.pendingBtn}
              >
                {formatShortDate(date)}
              </Button>
            ))}
          </Box>
        </>
      ) : (
        <Box className="flexRow" sx={sx.metaRow}>
          <Box className="flex1" sx={{ minWidth: 0 }}>
            <Typography sx={sx.metaLabel}>Prod</Typography>
            <Typography sx={sx.metaValue}>{formatProdDates(prodDatesToShow)}</Typography>
          </Box>
          <Box className="flex0" sx={{ minWidth: 0 }}>
            <Typography sx={sx.metaLabel}>DLC</Typography>
            <Typography sx={sx.metaValue}>{dlcDate ? formatShortDate(dlcDate) : "—"}</Typography>
          </Box>
          <Box className="flex1" sx={{ minWidth: 0,textAlign: "right" }}>
            <Typography sx={sx.metaLabel}>Foodcost</Typography>
            <Typography sx={sx.metaValue}>{formatFoodcost(recipe.foodcost)}</Typography>
          </Box>
        </Box>
      )}

      {onRemove && (
        <IconButton
          size="small"
          className="recipeCardRemoveBtn"
          data-no-drag="true"
          onClick={onRemove}
          sx={sx.removeBtn}
          aria-label="Retirer la recette"
        >
          <CloseIcon sx={{ fontSize: "14px" }} />
        </IconButton>
      )}
    </Box>
  )
}

export default RecipeCard

const sx = {
  card: {
    position: "relative", border: "1px solid", borderColor: COLORS.DEFAULT_GREY, borderRadius: "10px", p: "12px",
  },
  cardPending: { border: "2px solid", borderColor: COLORS.PRIMARY_COLOR },
  cardDone: { border: "2px solid", borderColor: COLORS.PRIMARY_COLOR, opacity: 0.85 },
  cardBlocked: { opacity: 0.6 },
  typeLabel: { fontSize: 14, fontWeight: 600, color: COLORS.GREY_700 },
  typeIcon: { fontSize: 18 },
  ref: { fontSize: 16, fontWeight: 500, color: COLORS.GREY_900, mt: "6px" },
  title: { fontSize: 16, fontWeight: 600, color: COLORS.GREY_900, lineHeight: 1.3, mt: "2px" },
  divider: { height: "1px", bgcolor: COLORS.DEFAULT_GREY, my: "8px" },
  pendingLabel: { fontSize: 12, fontWeight: 600, color: COLORS.PRIMARY_COLOR },
  pendingRow: { gap: "6px", mt: "6px" },
  pendingBtn: {
    minWidth: 0, px: "10px", py: "2px", fontSize: 12, textTransform: "none", borderRadius: "8px", backgroundColor: COLORS.WHITE,
    borderColor: COLORS.PRIMARY_COLOR, color: COLORS.PRIMARY_COLOR,
  },
  metaRow: { gap: "8px", mt: "10px" },
  metaLabel: { fontSize: 12, fontWeight: 500, color: COLORS.GREY_700, textTransform: "uppercase", letterSpacing: ".04em" },
  metaValue: { fontSize: 14, fontWeight: 500, color: COLORS.GREY_900, mt: "2px" },
  removeBtn: {
    position: "absolute", top: "-8px", right: "-8px", color: COLORS.DISABLED,
    width: "20px", height: "20px", p: 0,
    bgcolor: COLORS.WHITE, boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    opacity: 0, transition: "opacity .15s ease",
    "&:hover": { color: COLORS.WHITE, bgcolor: COLORS.RED_ERROR },
  },
}
